// Match-card captain authority (T3, delegation-visibility audit) — pure
// gating helpers, mirroring the backend's isActiveSideCaptain EXACTLY
// (matchCardV2.ts): a captain qualifies for a match SIDE only when their
// delegation grant's teamNo matches that side's own team-entry number
// (match.clan1TeamNo/clan2TeamNo — stamped once per side at match-create
// time, same for every slot on that side, since a match side is always ONE
// team-entry). A non-team-scoped/legacy side (sideTeamNo undefined) never
// has a captain concept, same as the backend's own early-return.
//
// viewerCaptainOf is keyed by the RAW clan identity matchCardV2's
// sideClanId() emits (`side === 1 ? match.clan1 : match.clan2`, verbatim —
// NOT the CLAN#-stripped or display-formatted form). MatchCardLive.tsx
// already threads this exact raw value into SlotGrid as clan1Id/clan2Id, so
// callers here should pass those same props straight through — no re-keying.
//
// Run: npx vitest run src/lib/matchCaptainAccess.test.ts

import type { MatchCardStatus } from './api';

export type ViewerCaptainOf = Record<string, number[]> | undefined;

/**
 * Does the viewer hold an ACTIVE captain grant for the SPECIFIC team that
 * plays as `clanKey` in THIS match? Mirrors isActiveSideCaptain's own
 * contract: an undefined/null sideTeamNo (legacy/non-team side) never
 * qualifies, and a grant on some OTHER team of the same clan never
 * qualifies either — only an exact team match affirms.
 */
export function isMatchSideCaptain(
  viewerCaptainOf: ViewerCaptainOf,
  clanKey: string | undefined,
  sideTeamNo: number | null | undefined,
): boolean {
  if (!clanKey || typeof sideTeamNo !== 'number') return false;
  const teams = viewerCaptainOf?.[clanKey];
  return Array.isArray(teams) && teams.includes(sideTeamNo);
}

/**
 * Poke-button eligibility (SlotGrid's canPokeSideN) — an OWN-SIDE nudge
 * (2026-07-21 Lisa product ruling, overrides the shipped L7b design):
 * authority over side X (its leader, an active team captain of side X's
 * exact teamNo, or admin) lets you poke side X's OWN outstanding pending
 * slots — "each house chases its own." Cross-side poking no longer exists —
 * a captain/leader who only holds authority on the OPPOSING side does not
 * qualify here. Mirrors handlePoke's isSideLeader/isSideCaptain/admin-trio
 * gate exactly (matchCardV2.ts): pass THIS side's own leader flag/clanKey/
 * teamNo, not the opposing side's. Admin has no side of its own and may
 * poke either — admin/leader behaviour for THIS side is otherwise
 * untouched, the captain leg is ORed in on top.
 */
export function canPokeSide(opts: {
  status: MatchCardStatus;
  pendingCount: number;
  isAdmin: boolean;
  /** Is the viewer the LEADER of THIS side (the one being poked)? */
  isSideLeader: boolean;
  viewerCaptainOf: ViewerCaptainOf;
  /** THIS side's own raw clan key — e.g. match.clan1 for a poke targeting side 1. */
  sideClanKey?: string;
  /** THIS side's own team-entry number — e.g. match.clan1TeamNo. */
  sideTeamNo?: number | null;
}): boolean {
  return (
    opts.status === 'LIVE' &&
    opts.pendingCount > 0 &&
    (opts.isAdmin ||
      opts.isSideLeader ||
      isMatchSideCaptain(opts.viewerCaptainOf, opts.sideClanKey, opts.sideTeamNo))
  );
}

/**
 * Proxy-upload eligibility for ONE side's slot table (feeds canProxyUpload,
 * which SlotChip also reuses verbatim for declare-nothing — see its own doc
 * comment: "canUpload's scope ... is EXACTLY the same authorization
 * handleDeclareNothing enforces server-side"). Own-side, own-team only —
 * mirrors handleDeclareNothing's isSlotOwner/isSideLeader/isSideCaptain/
 * admin-trio gate. Admin/leader behaviour is untouched — captain is ORed in.
 */
export function canProxyUploadSide(opts: {
  isAdmin: boolean;
  /** Is the viewer the LEADER of THIS side (the one being rendered)? */
  isSideLeader: boolean;
  viewerCaptainOf: ViewerCaptainOf;
  /** THIS side's own raw clan key — e.g. match.clan1 for clan1's table. */
  sideClanKey?: string;
  /** THIS side's own team-entry number — e.g. match.clan1TeamNo. */
  sideTeamNo?: number | null;
}): boolean {
  return (
    opts.isAdmin ||
    opts.isSideLeader ||
    isMatchSideCaptain(opts.viewerCaptainOf, opts.sideClanKey, opts.sideTeamNo)
  );
}
